import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../shared/utils.js/axiosConfig";

const initialState = {
  penaltyTypes: [],
  currentPenaltyType: null,
  pagination: {
    current_page: 1,
    per_page: 10,
    total: 0,
    last_page: 1,
  },
  loading: false,
  error: null,
};

// Fetch all penalty types with pagination
export const fetchPenaltyTypes = createAsyncThunk(
  "penaltyTypes/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/penalty-types", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Fetch single penalty type by ID
export const fetchPenaltyTypeById = createAsyncThunk(
  "penaltyTypes/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/penalty-types/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Create new penalty type
export const createPenaltyType = createAsyncThunk(
  "penaltyTypes/create",
  async (penaltyTypeData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/penalty-types",
        penaltyTypeData,
      );
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Update penalty type
export const updatePenaltyType = createAsyncThunk(
  "penaltyTypes/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`/penalty-types/${id}`, data);
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Toggle penalty type status
export const togglePenaltyTypeStatus = createAsyncThunk(
  "penaltyTypes/toggleStatus",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.patch(
        `/penalty-types/${id}/toggle-status`,
      );
      return { ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Delete penalty type
export const deletePenaltyType = createAsyncThunk(
  "penaltyTypes/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/penalty-types/${id}`);
      return { id, ...response.data, isOffline: response.isOfflineOptimistic };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Export PDF for single penalty type
export const exportPenaltyTypePDF = createAsyncThunk(
  "penaltyTypes/exportPDF",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/penalty-types/${id}/pdf`, {
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Export PDF for all penalty types
export const exportAllPenaltyTypesPDF = createAsyncThunk(
  "penaltyTypes/exportAllPDF",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/penalty-types/pdf", {
        params,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

// Export Excel for all penalty types
export const exportPenaltyTypesExcel = createAsyncThunk(
  "penaltyTypes/exportExcel",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get("/penalty-types/excel", {
        params,
        responseType: "blob",
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

const penaltyTypesSlice = createSlice({
  name: "penaltyTypes",
  initialState,
  reducers: {
    clearCurrentPenaltyType: state => {
      state.currentPenaltyType = null;
    },
    clearError: state => {
      state.error = null;
    },
    hydrate: (state, action) => {
      return { ...state, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      // Fetch all penalty types
      .addCase(fetchPenaltyTypes.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPenaltyTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.penaltyTypes = action.payload.data || [];
        state.pagination = action.payload.meta || initialState.pagination;
      })
      .addCase(fetchPenaltyTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch single penalty type
      .addCase(fetchPenaltyTypeById.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPenaltyTypeById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPenaltyType = action.payload.data;
      })
      .addCase(fetchPenaltyTypeById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create penalty type
      .addCase(createPenaltyType.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPenaltyType.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.data) {
          state.penaltyTypes.unshift(action.payload.data);
          state.pagination.total += 1;
        }
      })
      .addCase(createPenaltyType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update penalty type
      .addCase(updatePenaltyType.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePenaltyType.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.penaltyTypes.findIndex(
          pt => pt.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.penaltyTypes[index] = action.payload.data;
        }
      })
      .addCase(updatePenaltyType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Toggle Status
      .addCase(togglePenaltyTypeStatus.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(togglePenaltyTypeStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.penaltyTypes.findIndex(
          pt => pt.id === action.payload.data?.id,
        );
        if (index !== -1 && action.payload.data) {
          state.penaltyTypes[index] = action.payload.data;
        }
      })
      .addCase(togglePenaltyTypeStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete penalty type
      .addCase(deletePenaltyType.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePenaltyType.fulfilled, (state, action) => {
        state.loading = false;
        state.penaltyTypes = state.penaltyTypes.filter(
          pt => pt.id !== action.payload.id,
        );
        state.pagination.total -= 1;
      })
      .addCase(deletePenaltyType.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Export PDF
      .addCase(exportPenaltyTypePDF.pending, state => {
        state.loading = true;
      })
      .addCase(exportPenaltyTypePDF.fulfilled, state => {
        state.loading = false;
      })
      .addCase(exportPenaltyTypePDF.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Export All PDF
      .addCase(exportAllPenaltyTypesPDF.pending, state => {
        state.loading = true;
      })
      .addCase(exportAllPenaltyTypesPDF.fulfilled, state => {
        state.loading = false;
      })
      .addCase(exportAllPenaltyTypesPDF.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Export Excel
      .addCase(exportPenaltyTypesExcel.pending, state => {
        state.loading = true;
      })
      .addCase(exportPenaltyTypesExcel.fulfilled, state => {
        state.loading = false;
      })
      .addCase(exportPenaltyTypesExcel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentPenaltyType, clearError } =
  penaltyTypesSlice.actions;
export default penaltyTypesSlice.reducer;
